import { ResponseProgress, type ResponseProgressStepDetail } from "@design";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { formatDuration } from "@/i18n/format";
import type { ToolCallView } from "../chatStreamReducer";
import { currentStep, finishedSteps, type StepView } from "./replySteps";
import { useSecondsSince } from "./useSecondsSince";

function ElapsedTime({ startedAt }: Readonly<{ startedAt: string }>) {
  return <>{formatDuration(Math.floor(useSecondsSince(startedAt)))}</>;
}

/** What a step was asked and found (Figma "Detail"); none when it has neither. */
function detailOf(t: TFunction<"chat">, step: StepView): ResponseProgressStepDetail | undefined {
  if (!step.query && step.found.length === 0) return undefined;
  return {
    title: step.found.length > 0 ? t("progress.found", { count: step.found.length }) : undefined,
    lines: step.query ? [t("progress.query", { query: step.query })] : [],
    items: step.found,
  };
}

/**
 * The reply while it works (Figma "Generating message"): dots, the current step and how long it has taken, with the
 * steps so far a click away. `startedAt` is when the user sent the message (ISO); without it there is no counter.
 */
export function ReplyProgress({
  tools,
  writing,
  startedAt,
}: Readonly<{ tools: ToolCallView[]; writing: boolean; startedAt?: string }>) {
  const { t } = useTranslation("chat");
  const now = currentStep(t, tools, writing);
  return (
    <ResponseProgress
      label={now.label}
      labelDetail={now.query ? t("progress.querySuffix", { query: now.query }) : undefined}
      elapsed={startedAt ? <ElapsedTime startedAt={startedAt} /> : undefined}
      steps={finishedSteps(t, tools).map((step) => ({
        id: step.id,
        label: step.label,
        status: step.status,
        detail: detailOf(t, step),
      }))}
    />
  );
}
